import { zodResolver } from "@hookform/resolvers/zod";
import type { JobDetailsSection } from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import type { DesignConditionsData, DesignConditionsSection } from "@stryker/core/types/sections";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";
import { farenheitToCelcius, getFloat } from "@/utils/mappings";

const cardName = "designConditions";

type DesignConditionsProps = {
  temperatureUnitsOptions: { title: string; value: string }[];
  savedDesignConditions: DesignConditionsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setDesignConditions: (payload: { data: DesignConditionsData; status: ExpandStatus }) => void;
  jobDetails: JobDetailsSection;
};

export function DesignConditions({
  temperatureUnitsOptions,
  savedDesignConditions,
  toggleExpandableSection,
  setDesignConditions,
  jobDetails,
}: DesignConditionsProps) {
  const { repairSystems } = useConfigOptions();

  const selectedRepairSystem = repairSystems?.find((r) => r.id == jobDetails.data.repair_system);

  // Schema and form setup
  const designConditionsSchema = z
    .object({
      design_pressure: z.union([
        z.number().gte(-15).lt(maxInputNumber),
        z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
      ]),
      design_temperature: z.union([
        z.number().lte(maxInputNumber),
        z.string().refine((value) => value === "" || !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
      ]),
      unit_for_design_temperature: z.string().min(1, "Required"),
    })
    .superRefine((data, ctx) => {
      // Only validate if design_temperature is a number
      if (typeof data.design_temperature === "number") {
        const maxTemp =
          data.unit_for_design_temperature === "Celsius" && selectedRepairSystem?.repair_system_max_temp
            ? farenheitToCelcius(selectedRepairSystem.repair_system_max_temp)
            : (selectedRepairSystem?.repair_system_max_temp ?? maxInputNumber);

        if (data.design_temperature > maxTemp) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Design temperature must not exceed ${maxTemp.toFixed(0)}°${data.unit_for_design_temperature === "Celsius" ? "C" : "F"} (max repair system temperature)`,
            path: ["design_temperature"],
          });
        }
      }
    });

  type DesignConditionsSchema = z.infer<typeof designConditionsSchema>;
  const form = useForm<DesignConditionsSchema>({
    resolver: zodResolver(designConditionsSchema),
    values: {
      design_pressure: savedDesignConditions.data.design_pressure ?? "",
      design_temperature: savedDesignConditions.data.design_temperature ?? "",
      unit_for_design_temperature: savedDesignConditions.data.unit_for_design_temperature ?? "Fahrenheit",
    },
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = form;

  const unit_for_design_temperature = watch("unit_for_design_temperature");

  // Ensure default to Fahrenheit if no saved value
  useEffect(() => {
    const currentValue = form.getValues("unit_for_design_temperature");
    if (!currentValue) {
      setValue("unit_for_design_temperature", "Fahrenheit", { shouldValidate: true });
    }
  }, []);

  const repairSystemMaxTemp =
    unit_for_design_temperature === "Celsius" && selectedRepairSystem?.repair_system_max_temp
      ? farenheitToCelcius(selectedRepairSystem.repair_system_max_temp)
      : (selectedRepairSystem?.repair_system_max_temp ?? maxInputNumber);

  // Re-validate temperature upper bound depending on unit/repair system
  useEffect(() => {
    // Trigger validation for design_temperature when unit changes to update messages if any
    form.trigger("design_temperature");
  }, [unit_for_design_temperature, selectedRepairSystem]);

  const handleSaveDesignConditions = (data: DesignConditionsSchema) => {
    setDesignConditions({
      data,
      status: "done",
    });
  };

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedDesignConditions.status}
      title="Design Conditions"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(handleSaveDesignConditions)} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Design pressure"
              type="number"
              step={inputStep}
              error={errors.design_pressure?.message}
              {...register("design_pressure", { setValueAs: getFloat })}
              endAdornment={
                <Typography variant="text-small" weight="regular" className="text-gray-400">
                  psi
                </Typography>
              }
            />
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Design temperature"
              type="number"
              step={inputStep}
              error={errors.design_temperature?.message}
              {...register("design_temperature", { setValueAs: getFloat })}
            />

            <div className="flex-1 basis-full md:basis-56">
              <FormField
                control={form.control}
                name="unit_for_design_temperature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit for design temperature</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(option) => {
                          field.onChange(option);
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            !field.value && "[&>span]:text-gray-400",
                            errors.unit_for_design_temperature && "border-error-300"
                          )}
                        >
                          <SelectValue placeholder="Unit for design temperature" />
                        </SelectTrigger>
                        <SelectContent>
                          {temperatureUnitsOptions.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <Button type="submit" className="self-end">
            Save
          </Button>
        </form>
      </Form>
    </ExpandableSection>
  );
}
